import React from 'react';
import Editor from '@monaco-editor/react';
import { getLanguageFromSkills } from '../utils/languageDetection';

/**
 * CodeSnippetViewer Component
 * A reusable read-only Monaco Editor for displaying code with syntax highlighting
 * 
 * @param {string} code - The code to display
 * @param {Array|string} skillsTested - Skills array or language string for detection
 * @param {string} language - Optional: Pre-detected language (overrides skillsTested)
 * @param {number} maxHeight - Maximum height in pixels (default: 400)
 */
function CodeSnippetViewer({ code, skillsTested, language, maxHeight = 400 }) {
  if (!code) return null;

  // Determine language: use explicit language prop, or detect from skillsTested
  const detectedLanguage = language || getLanguageFromSkills(skillsTested);
  
  // Calculate dynamic height based on content
  const lineCount = code.split('\n').length;
  const lineHeight = 21;
  const padding = 32; // top + bottom padding
  const calculatedHeight = Math.min((lineCount * lineHeight) + padding, maxHeight);

  return (
    <div className="rounded-md overflow-hidden border border-gray-700" style={{ padding: '0 16px', backgroundColor: '#1e1e1e' }}>
      <Editor
        height={`${calculatedHeight}px`}
        language={detectedLanguage}
        value={code}
        theme="vs-dark"
        options={{
          readOnly: true,
          domReadOnly: true,
          minimap: { enabled: false },
          scrollBeyondLastLine: false,
          fontSize: 14,
          fontFamily: '"Fira code", "Fira Mono", monospace',
          automaticLayout: true,
          lineNumbers: 'on',
          folding: false,
          renderLineHighlight: 'none',
          scrollbar: {
            vertical: 'auto',
            horizontal: 'auto',
            useShadows: false,
          },
          overviewRulerLanes: 0,
          hideCursorInOverviewRuler: true,
          overviewRulerBorder: false,
          wordWrap: 'on',
          contextmenu: false,
          selectOnLineNumbers: false,
          selectionHighlight: false,
          occurrencesHighlight: false,
          cursorStyle: 'line-thin',
          cursorBlinking: 'solid',
          glyphMargin: false,
          lineDecorationsWidth: 16,
          lineNumbersMinChars: 0,
          renderValidationDecorations: 'off',
          links: false,
          colorDecorators: false,
          guides: {
            indentation: false,
          },
          padding: {
            top: 16,
            bottom: 16,
            left: 16,
            right: 16,
          },
        }}
      />
    </div>
  );
}

export default CodeSnippetViewer;
